import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, UserMinus, UserPlus, Loader2 } from "lucide-react";

interface ManageChannelMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelId: string;
  channelName: string;
}

interface MemberProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
}

interface TeamMember {
  id: string;
  full_name: string | null;
  email: string | null;
}

const getInitials = (name: string | null) => {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

export const ManageChannelMembersDialog = ({
  open,
  onOpenChange,
  channelId,
  channelName,
}: ManageChannelMembersDialogProps) => {
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();

  // Fetch current members
  const { data: members = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ["channel-members", channelId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("channel_members")
        .select("id, user_id, profiles:user_id(id, full_name, email)")
        .eq("channel_id", channelId);

      if (error) throw error;

      return (data || []).map((m: any) => ({
        id: m.id,
        user_id: m.user_id,
        full_name: m.profiles?.full_name || null,
        email: m.profiles?.email || null,
      })) as MemberProfile[];
    },
    enabled: open,
  });

  // Fetch ALL team members once with a stable key — never includes dynamic member list
  const { data: allTeamMembers = [] } = useQuery({
    queryKey: ["all-team-members"],
    queryFn: async () => {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("user_id")
        .in("role", ["admin", "consultant", "marketing_and_sales", "finance", "reporting"]);

      if (!roles || roles.length === 0) return [];
      const userIds = [...new Set(roles.map((r) => r.user_id))];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds)
        .order("full_name");

      return (profiles || []) as TeamMember[];
    },
    enabled: open,
  });

  // Derive available members client-side — no query key instability
  const existingMemberIds = useMemo(
    () => new Set(members.map((m) => m.user_id)),
    [members]
  );

  const availableMembers = useMemo(
    () => allTeamMembers.filter((m) => !existingMemberIds.has(m.id)),
    [allTeamMembers, existingMemberIds]
  );

  const filteredAvailable = availableMembers.filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      m.full_name?.toLowerCase().includes(s) ||
      m.email?.toLowerCase().includes(s)
    );
  });

  const { mutate: addMember, isPending: isAdding } = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("channel_members")
        .insert({ channel_id: channelId, user_id: userId });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Member added");
      queryClient.invalidateQueries({ queryKey: ["channel-members", channelId] });
      queryClient.invalidateQueries({ queryKey: ["all-team-members"] });
    },
    onError: () => toast.error("Failed to add member"),
  });

  const { mutate: removeMember, isPending: isRemoving } = useMutation({
    mutationFn: async (membershipId: string) => {
      const { error } = await supabase
        .from("channel_members")
        .delete()
        .eq("id", membershipId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Member removed");
      queryClient.invalidateQueries({ queryKey: ["channel-members", channelId] });
      queryClient.invalidateQueries({ queryKey: ["all-team-members"] });
    },
    onError: () => toast.error("Failed to remove member"),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle>Manage Members — {channelName}</SheetTitle>
          <SheetDescription>
            Add or remove members from this channel.
          </SheetDescription>
        </SheetHeader>

        {/* Current Members */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Current Members ({members.length})</h4>
          {isLoadingMembers ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ScrollArea className="max-h-[160px]">
              <div className="space-y-1">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs">
                        {getInitials(member.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm flex-1 truncate">
                      {member.full_name || member.email}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => removeMember(member.id)}
                      disabled={isRemoving}
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Add Members */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Add Members</h4>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search team members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8"
            />
          </div>
          <ScrollArea className="max-h-[160px]">
            <div className="space-y-1">
              {filteredAvailable.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {search ? "No members found" : "All team members are already in this channel"}
                </p>
              ) : (
                filteredAvailable.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs">
                        {getInitials(member.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm flex-1 truncate">
                      {member.full_name || member.email}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                      onClick={() => addMember(member.id)}
                      disabled={isAdding}
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};
